import { resolveBrowserServiceUrl } from '@/shared/api/resolveServiceUrl';

export type WebSocketMessageType =
  | 'connected'
  | 'scan_execution_update'
  | 'scan_execution_log'
  | 'scan_execution_event'
  | 'scan_module_progress'
  | 'vulnerability_created'
  | 'vulnerability_updated'
  | 'notification_created'
  | 'notification_updated'
  | 'notifications_read'
  | 'notification_ephemeral'
  | 'unread_counts_updated'
  | 'pilot_subscribe'
  | 'pilot_unsubscribe'
  | 'pilot_subscribed'
  | 'pilot_updated'
  | 'ping'
  | 'pong';

export interface WebSocketMessage {
  type: WebSocketMessageType;
  company_id?: string;
  user_id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  timestamp: string;
}

export interface WebSocketConfig {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  fallbackMode?: boolean;
}

type InternalEvent =
  WebSocketMessageType | 'connected' | 'disconnected' | 'fallback_mode' | 'message' | 'error';

type Listener<T = unknown> = (payload?: T) => void;

const DEFAULT_WS_URL = 'wss://api.ctem.sentra-tech.ru/api/v1/ws';

function buildWebSocketUrl(): string {
  const configuredWsUrl = import.meta.env['VITE_WS_URL']?.trim();
  if (configuredWsUrl) {
    return resolveBrowserServiceUrl(configuredWsUrl);
  }

  const configuredApiUrl = import.meta.env['VITE_API_URL']?.trim();
  if (!configuredApiUrl) {
    return DEFAULT_WS_URL;
  }

  try {
    const url = new URL(resolveBrowserServiceUrl(configuredApiUrl), window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = `${url.pathname.replace(/\/$/, '')}/ws`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return DEFAULT_WS_URL;
  }
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;
  private fallbackMode = false;
  private fallbackNotificationShown = false;
  private listeners = new Map<InternalEvent, Set<Listener>>();
  private desiredPilotSubscriptions = new Map<string, number>();

  constructor(config: WebSocketConfig) {
    this.config = {
      url: config.url,
      reconnectInterval: config.reconnectInterval || 5000,
      maxReconnectAttempts: config.maxReconnectAttempts || 10,
      heartbeatInterval: config.heartbeatInterval || 30000,
      fallbackMode: config.fallbackMode || false,
    };
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.isIntentionallyClosed = false;

    try {
      // Добавляем credentials для передачи cookies
      this.ws = new WebSocket(this.config.url);
      this.setupEventHandlers();
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.handleConnectionFailure();
    }
  }

  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.fallbackMode = false;
    this.fallbackNotificationShown = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  send(message: Partial<WebSocketMessage>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          ...message,
          timestamp: new Date().toISOString(),
        })
      );
    } else {
      console.warn('[WebSocket] Cannot send message, connection not open');
    }
  }

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.fallbackMode = false;
      this.fallbackNotificationShown = false;
      this.emit('connected');
      this.desiredPilotSubscriptions.forEach((_count, pilotId) => {
        this.send({ type: 'pilot_subscribe', data: { pilot_id: pilotId } });
      });
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      this.emit('error', error);
    };

    this.ws.onclose = (event) => {
      this.emit('disconnected', event);

      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }

      if (!this.isIntentionallyClosed) {
        this.handleConnectionFailure();
      }
    };
  }

  private handleConnectionFailure(): void {
    if (this.reconnectAttempts === 0) {
      // Первый раз показываем уведомление о fallback режиме
      if (!this.fallbackNotificationShown) {
        this.fallbackMode = true;
        this.fallbackNotificationShown = true;
        this.emit('fallback_mode', {
          message:
            'WebSocket connection failed. Notifications will be limited. Attempting to reconnect...',
          retryInterval: this.config.reconnectInterval,
        });
      }
    }

    if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      // Достигнут лимит попыток, переходим в fallback режим
      this.fallbackMode = true;
      this.emit('fallback_mode', {
        message: 'WebSocket connection failed after maximum attempts. Running in fallback mode.',
        retryInterval: this.config.reconnectInterval,
      });

      // Продолжаем попытки переподключения в фоне
      this.scheduleBackgroundReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    this.reconnectAttempts++;
    const delay = Math.min(
      this.config.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1),
      30000 // Max 30 seconds
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private scheduleBackgroundReconnect(): void {
    if (this.reconnectTimer) return;

    // В fallback режиме пробуем переподключиться каждые 8 секунд
    const fallbackInterval = 8000;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts = 0; // Сбрасываем счетчик попыток
      this.connect();
    }, fallbackInterval);
  }

  private handleMessage(message: WebSocketMessage): void {
    // Emit general message event
    this.emit('message', message);

    // Emit specific event based on message type
    this.emit(message.type, message);

    // Handle ping/pong
    if (message.type === 'ping') {
      this.send({ type: 'pong' });
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }

    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, this.config.heartbeatInterval);
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isInFallbackMode(): boolean {
    return this.fallbackMode;
  }

  getReadyState(): number | undefined {
    return this.ws?.readyState;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  getMaxReconnectAttempts(): number {
    return this.config.maxReconnectAttempts;
  }

  subscribePilot(pilotId: string): void {
    const normalized = pilotId.trim();
    if (!normalized) return;
    const count = this.desiredPilotSubscriptions.get(normalized) ?? 0;
    this.desiredPilotSubscriptions.set(normalized, count + 1);
    if (count === 0 && this.isConnected()) {
      this.send({ type: 'pilot_subscribe', data: { pilot_id: normalized } });
    }
  }

  unsubscribePilot(pilotId: string): void {
    const normalized = pilotId.trim();
    if (!normalized) return;
    const count = this.desiredPilotSubscriptions.get(normalized) ?? 0;
    if (count > 1) {
      this.desiredPilotSubscriptions.set(normalized, count - 1);
      return;
    }
    this.desiredPilotSubscriptions.delete(normalized);
    if (count > 0 && this.isConnected()) {
      this.send({ type: 'pilot_unsubscribe', data: { pilot_id: normalized } });
    }
  }

  on<T = unknown>(event: InternalEvent, listener: Listener<T>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener as Listener);
  }

  off<T = unknown>(event: InternalEvent, listener: Listener<T>): void {
    this.listeners.get(event)?.delete(listener as Listener);
  }

  private emit(event: InternalEvent, payload?: unknown): void {
    const listeners = this.listeners.get(event);
    if (!listeners) {
      return;
    }
    listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error(`[WebSocket] Listener error for event "${event}":`, error);
      }
    });
  }
}

// Singleton instance
let wsInstance: WebSocketService | null = null;

// Функция для сброса WebSocket (для отладки)
export function resetWebSocketService(): void {
  if (wsInstance) {
    wsInstance.disconnect();
    wsInstance = null;
  }
}

export function getWebSocketService(): WebSocketService {
  if (!wsInstance) {
    wsInstance = new WebSocketService({
      url: buildWebSocketUrl(),
      fallbackMode: true, // Включаем fallback режим
    });
  }

  return wsInstance;
}
